const IMG = "https://static.prod-images.emergentagent.com/jobs/c204c004-4e3b-4de6-bafd-66db5d32f428/images";

export const FLEET_SCALE = "12,000+ vehicles · 14 locations across the UAE";

export const HUB = {
  headline: "PARTNER WITH EUROPCAR.",
  sub: "Put a global mobility brand behind your business. From hotel concierge desks to corporate fleets, we build rental programmes that make your customers' journeys easier — and your revenue stronger.",
  image: `${IMG}/3cffa4f56cb2e5cfc1e4a4572882ae45900dd509087c46f9be1fbd99b50a6730.jpeg`,
  valueStrip: [FLEET_SCALE, "Dedicated partnership owner for every account", "Response within your category's SLA — always"],
  why: [
    { title: "A brand your customers already trust", text: "Europcar is one of the world's most recognised mobility names — your recommendation carries no risk and plenty of weight." },
    { title: "Operations built for partners", text: "Priority availability, delivery to your property or office, and service standards written into the programme — not left to chance." },
    { title: "Revenue without overhead", text: "You bring the customer relationship; we bring the cars, insurance, maintenance and support. No fleet on your books, ever." },
  ],
  how: [
    { step: "Tell us about your business", text: "Pick your category below and send the enquiry — two minutes, no documents needed." },
    { step: "Meet your partnership owner", text: "A named manager responds within the category SLA to qualify the fit and shape the model." },
    { step: "Launch your programme", text: "We agree terms, onboard your team and go live — with a clear escalation line from day one." },
  ],
  closing: { headline: "Ready to move together?", text: "Choose your category and start the conversation — your partnership owner is waiting.", cta: "Start a partnership enquiry" },
};

export const PARTNER_FAQ = [
  { q: "How quickly will Europcar respond to my enquiry?", a: "Every category has a named owner and a service-level target — corporate mobility and insurance enquiries are answered within 4 business hours; most others within 1–2 business days. You'll see the commitment confirmed on your enquiry receipt." },
  { q: "Do I need technical integration or an API to partner?", a: "No. Many of our strongest partnerships run entirely on concierge referrals, dedicated phone lines and agreed processes. Where digital integration adds value, we scope it together after the partnership is agreed." },
  { q: "Are trade rates or commissions published?", a: "No — commercial terms are agreed privately per partnership, based on volume, category and commitment. Nothing commercial is published on this site." },
  { q: "Can a small property or independent agency partner with Europcar?", a: "Yes. Programmes scale from a single concierge desk arrangement to multi-property agreements — the enquiry form asks about your volumes so we propose the right starting model." },
  { q: "Is there a partner portal?", a: "Approved partners receive access to a secure partner portal for bookings and account management, configured during onboarding after your agreement is signed." },
  { q: "Which markets do you cover?", a: "Our own operation covers Dubai and the Northern Emirates with 12,000+ vehicles across 14 locations, and the wider Europcar network extends coverage globally for international programmes." },
];

const SHARED_SAFEGUARDS = [
  "No commercial terms, trade rates or commissions are published — everything is agreed privately.",
  "No API or technical build is promised upfront; integration is scoped only where it adds value.",
  "Your customer data is used solely to operate the agreed programme.",
];

export const CATEGORIES = [
  {
    slug: "hotels-hospitality",
    label: "Hotels & Hospitality",
    headline: "YOUR GUESTS' JOURNEY, EXTENDED.",
    sub: "Give every guest a car as polished as your property — delivered to your porte-cochère, handled by our team, credited to your service reputation.",
    image: `${IMG}/f1d810eca5617a5070b7e5ee7d0e549bf834fda6b7520af9a00797686d2203cd.jpeg`,
    models: [
      { name: "Concierge referral", text: "Your concierge recommends; we deliver to the lobby within hours. Zero admin for your team beyond the introduction." },
      { name: "In-property desk", text: "A staffed or virtual Europcar presence inside your property for high-volume hotels and residences." },
      { name: "Extended-stay programme", text: "Monthly vehicles bundled into long-stay and serviced-apartment packages, renewed alongside the room." },
    ],
    journey: [
      ["Guest asks concierge for a car", "Concierge sends one WhatsApp or portal request"],
      ["We confirm within the SLA", "Vehicle and rate confirmed back to the desk"],
      ["Delivery to your entrance", "Documents verified digitally — no paperwork at the desk"],
      ["Guest drives, you're credited", "Service recorded under your property's programme"],
    ],
    values: ["Doorstep delivery to your property, daily 09:00–21:00", "Guest-ready fleet from city cars to luxury SUVs", "Your guests deal with one trusted brand — and thank your desk for it"],
    endCustomer: "Your guests always see clear, VAT-inclusive pricing and the same insurance cover as any Europcar customer — no special terms to explain, no surprises at your front desk.",
    cta: "Enquire about a hospitality partnership",
    qualification: [
      { name: "property_name", label: "Property / group name", type: "text" },
      { name: "keys", label: "Number of rooms / keys", type: "select", options: ["Under 100", "100–300", "300–600", "600+"] },
      { name: "star_rating", label: "Property rating", type: "select", options: ["3-star", "4-star", "5-star", "Serviced apartments / other"] },
      { name: "guest_profile", label: "Main guest profile", type: "select", options: ["Leisure / tourist", "Business", "Long-stay", "Mixed"] },
    ],
  },
  {
    slug: "travel-tourism",
    label: "Travel, Tourism & DMCs",
    headline: "BUILD THE DRIVE INTO THE ITINERARY.",
    sub: "From airport pickup to desert-road day trips — add reliable self-drive mobility to every package you sell.",
    image: `${IMG}/dd2c1492f7fd8d1d643d2f327f658ff9f02fb2171a8bf7e87bed680605934a75.jpeg`,
    models: [
      { name: "Package inclusion", text: "Bundle rentals into tours and packages at agreed programme terms." },
      { name: "Agent referral", text: "Your consultants book on behalf of clients through a dedicated trade line." },
      { name: "Group & event mobility", text: "Multi-vehicle allocations for incentive groups, film crews and events." },
    ],
    journey: [
      ["You confirm the itinerary", "Send vehicle needs with the manifest"],
      ["We allocate the fleet", "Vehicle classes confirmed, airport or hotel handover"],
      ["Guests collect or receive cars", "24/7 airport desks, flight tracking included"],
      ["One consolidated account", "Single point of billing and support for your operation"],
    ],
    values: ["24/7 desks at DXB T1, T3 and Sharjah Airport", "Fleet breadth for singles, families and VIP groups", "Flight-tracked pickups — late arrivals never lose their car"],
    endCustomer: "Travellers get standard Europcar cover, unlimited UAE mileage on daily/weekly rentals and transparent VAT-inclusive pricing — your brand stays front and centre.",
    cta: "Enquire about a travel trade partnership",
    qualification: [
      { name: "company_type", label: "Business type", type: "select", options: ["Travel agency", "Tour operator", "DMC", "OTA / online travel", "Other"] },
      { name: "licence", label: "Tourism licence number (optional)", type: "text" },
      { name: "annual_volume", label: "Annual travellers handled", type: "select", options: ["Under 1,000", "1,000–10,000", "10,000–50,000", "50,000+"] },
      { name: "source_markets", label: "Main source markets", type: "text" },
    ],
  },
  {
    slug: "digital-aggregators",
    label: "Digital Aggregators & OTAs",
    headline: "INVENTORY YOUR USERS CAN TRUST.",
    sub: "List a fleet that shows up — clean cars, honest availability and a brand your marketplace is proud to display.",
    image: `${IMG}/f0f33e2ba33fad420c015360d93c91a4bb5f879bb14f804ec6b9fc2ff8758261.jpeg`,
    models: [
      { name: "Managed listings", text: "We maintain accurate vehicle, rate and availability content on your platform." },
      { name: "Referral hand-off", text: "Qualified traffic completes booking with Europcar; attribution is agreed per campaign." },
      { name: "Deeper integration", text: "Where volume justifies it, technical integration is scoped post-agreement — never promised upfront." },
    ],
    journey: [
      ["User finds Europcar on your platform", "Accurate content, honest availability"],
      ["Booking or referral hand-off", "Per the agreed operating model"],
      ["We fulfil and support", "Delivery, counters and 24/7 roadside on us"],
      ["Performance reviewed together", "Regular reporting against agreed measures"],
    ],
    values: ["A recognised global brand raises marketplace trust", "Operational fulfilment handled end-to-end by our team", "Content accuracy owned by a named partnership manager"],
    endCustomer: "Users booking through your platform receive identical service, insurance and pricing transparency to direct customers — their experience protects your platform's reputation.",
    cta: "Enquire about listing Europcar",
    qualification: [
      { name: "platform_name", label: "Platform name & URL", type: "text" },
      { name: "monthly_bookings", label: "Monthly car-rental bookings on platform", type: "select", options: ["Pre-launch", "Under 500", "500–5,000", "5,000+"] },
      { name: "integration_model", label: "Preferred operating model", type: "select", options: ["Managed listings", "Referral hand-off", "To be discussed"] },
      { name: "markets_served", label: "Markets served", type: "text" },
    ],
  },
  {
    slug: "corporate-mobility",
    label: "Corporate Mobility",
    headline: "YOUR TEAM, ALWAYS MOVING.",
    sub: "Replace fleet ownership headaches with a corporate rental programme — pooled vehicles, monthly staff cars and visiting-executive service under one account.",
    image: `${IMG}/75e1da7b091ec215b8039979b1c36a092f001529960e30c235ac3639641bbd43.jpeg`,
    models: [
      { name: "Corporate monthly fleet", text: "Staff vehicles on rolling or fixed terms with maintenance, insurance and replacement included." },
      { name: "Project & pool cars", text: "Scale up for projects and down when they close — no residual-value risk." },
      { name: "Executive & visitor service", text: "Airport pickups and premium vehicles for visiting leadership and clients." },
    ],
    journey: [
      ["Scope the fleet together", "Vehicle mix, terms and delivery points agreed"],
      ["One master agreement", "Consolidated billing, named account owner"],
      ["Cars delivered to your offices", "Free monthly delivery in Dubai & Sharjah"],
      ["Flex as you grow", "Add, swap or return vehicles with a service request"],
    ],
    values: ["Zero fleet capex and no disposal risk", "Maintenance, registration, insurance and roadside included", "4-business-hour response SLA on corporate enquiries"],
    endCustomer: "Your employees drive fully insured, maintained vehicles with 24/7 roadside support — HR and finance see one clean monthly invoice.",
    cta: "Enquire about corporate mobility",
    qualification: [
      { name: "company_name_q", label: "Company name", type: "text" },
      { name: "fleet_size", label: "Vehicles required", type: "select", options: ["1–5", "6–20", "21–50", "50+"] },
      { name: "contract_length", label: "Expected term", type: "select", options: ["1–3 months", "3–6 months", "6–12 months", "12+ months"] },
      { name: "employees", label: "Company size", type: "select", options: ["Under 50", "50–250", "250–1,000", "1,000+"] },
    ],
  },
  {
    slug: "insurance-roadside",
    label: "Insurance & Roadside Partners",
    headline: "REPLACEMENT CARS, WITHOUT THE SCRAMBLE.",
    sub: "When your policyholder's car is off the road, ours is already on the way — courtesy and replacement vehicle programmes with real SLAs.",
    image: `${IMG}/7ae3352e47f1e9bffff35e7d1a849da2f46855d5598d7074b866acc6e31d0029.jpeg`,
    models: [
      { name: "Claims replacement programme", text: "Pre-agreed vehicle classes and rates for repair-duration replacements." },
      { name: "Roadside courtesy supply", text: "Rapid vehicle supply to your assistance network's stranded customers." },
      { name: "Garage & bodyshop partnerships", text: "Replacement cars offered at the point of repair intake." },
    ],
    journey: [
      ["Claim or breakdown logged", "Your team raises one standardised request"],
      ["Vehicle allocated within SLA", "Class-matched to the policy entitlement"],
      ["Delivered to customer or garage", "Documents verified digitally"],
      ["Consolidated billing", "Per-claim reporting your finance team can audit"],
    ],
    values: ["4-business-hour response SLA on partner requests", "Fleet depth across every class — city car to 7-seat SUV", "Clean per-claim audit trail for your finance team"],
    endCustomer: "Policyholders receive an insured, maintained vehicle with the same cover disclosures as any Europcar rental — their experience reflects well on your claims service.",
    cta: "Enquire about replacement programmes",
    qualification: [
      { name: "company_name_q", label: "Company name", type: "text" },
      { name: "monthly_replacements", label: "Replacement vehicles needed per month", type: "select", options: ["Under 10", "10–50", "50–200", "200+"] },
      { name: "response_sla", label: "Response SLA you require", type: "select", options: ["Same day", "Within 4 hours", "Within 24 hours"] },
      { name: "coverage_emirates", label: "Emirates you need covered", type: "text" },
    ],
  },
  {
    slug: "loyalty-programs",
    label: "Loyalty Programmes",
    headline: "A REWARD WORTH REDEEMING.",
    sub: "Add car rental to your earn-and-burn catalogue — a high-value, high-frequency reward your members actually use.",
    image: `${IMG}/d148897060034c7dc97340ce87b6c941e6ad6e259da52dfa5855c590965d8104.jpeg`,
    models: [
      { name: "Redemption partner", text: "Members redeem points against rentals at agreed programme value." },
      { name: "Earn partner", text: "Members earn your currency on qualifying Europcar rentals." },
      { name: "Member benefits tier", text: "Preferential treatment — upgrades, priority delivery — for your top tiers." },
    ],
    journey: [
      ["Member sees the reward", "Rental redemption in your catalogue"],
      ["Redemption confirmed", "Simple flow agreed with your programme team"],
      ["Member drives", "Full standard service, insurance and support"],
      ["Reconciliation", "Clean monthly settlement between programmes"],
    ],
    values: ["High perceived value with everyday utility", "No points liability sitting on our side of the ledger unmanaged", "Programme mechanics agreed privately — nothing published"],
    endCustomer: "Members enjoy the full Europcar experience — transparent VAT-inclusive pricing on any cash component and identical insurance cover.",
    cta: "Enquire about a loyalty partnership",
    qualification: [
      { name: "programme_name", label: "Programme name", type: "text" },
      { name: "member_base", label: "Active member base", type: "select", options: ["Under 50k", "50k–250k", "250k–1M", "1M+"] },
      { name: "redemption_model", label: "Preferred model", type: "select", options: ["Redemption only", "Earn only", "Earn + redemption", "Benefits tier"] },
      { name: "points_currency", label: "Points currency name", type: "text" },
    ],
  },
  {
    slug: "affiliate-sales",
    label: "Affiliate Sales",
    headline: "TURN YOUR AUDIENCE INTO JOURNEYS.",
    sub: "Publishers, creators and comparison sites — send your audience to a booking experience that converts, and get credited for it.",
    image: `${IMG}/d5e660e31f3f450d411dc7c3bcf2e9bb391c9723fd75d1d1f95acc9dedd8d649.jpeg`,
    models: [
      { name: "Tracked referrals", text: "Campaign-tagged links credit your channel for completed bookings." },
      { name: "Content partnerships", text: "Co-created guides and reviews with vehicles supplied for editorial use." },
      { name: "Seasonal campaigns", text: "Offer-led pushes around peak travel windows, planned together." },
    ],
    journey: [
      ["Audience clicks your link", "UTM-tagged, attributed to your channel"],
      ["Booker completes on our site", "A 3-minute, conversion-tuned flow"],
      ["Attribution recorded", "Per the agreed campaign measurement"],
      ["You're credited", "Terms agreed privately per partnership"],
    ],
    values: ["A booking flow that respects your traffic — fast, honest, mobile-first", "Creative support and priority fleet access for content", "Attribution measurement agreed before the first click"],
    endCustomer: "Your audience gets the standard Europcar experience — published VAT-inclusive prices, zero-deposit options and full insurance disclosure. Their trust in you stays intact.",
    cta: "Enquire about affiliate partnership",
    qualification: [
      { name: "channel", label: "Website / channel URL", type: "text" },
      { name: "audience_size", label: "Monthly audience", type: "select", options: ["Under 10k", "10k–100k", "100k–1M", "1M+"] },
      { name: "promo_channels", label: "Main promotion channels", type: "select", options: ["Website / SEO", "YouTube / video", "Instagram / TikTok", "Email / newsletter", "Mixed"] },
      { name: "expected_referrals", label: "Expected referrals per month", type: "select", options: ["Under 50", "50–250", "250–1,000", "1,000+"] },
    ],
  },
  {
    slug: "connect",
    label: "Connect With Us",
    headline: "DON'T SEE YOUR CATEGORY? LET'S TALK.",
    sub: "Real-estate developers, universities, government entities, event organisers — if your idea moves people, we want to hear it.",
    image: `${IMG}/82320359f852d5024b7b0dc9caec1a808bb1161c86b69f531ec13c1067271966.jpeg`,
    models: [
      { name: "Open proposal", text: "Describe the opportunity in your words — a partnership owner will shape it with you." },
      { name: "Community programmes", text: "Resident, student and employee mobility benefits for communities and campuses." },
      { name: "Events & activations", text: "Vehicle supply and brand collaborations for events and launches." },
    ],
    journey: [
      ["Share the idea", "A short enquiry — no documents needed"],
      ["We qualify together", "The Partnerships Team responds within 2 business days"],
      ["Shape the model", "We match the idea to a proven programme structure"],
      ["Launch", "Named owner, agreed terms, clear next steps"],
    ],
    values: ["Every enquiry is read by the Partnerships Team — not a form void", "Proven programme structures adapted to new categories", "Honest early answers — if it's not a fit, we'll say so quickly"],
    endCustomer: "Whatever the programme, end customers always receive standard Europcar service, cover and transparent pricing.",
    cta: "Start the conversation",
    qualification: [
      { name: "organisation", label: "Organisation name", type: "text" },
      { name: "idea", label: "Partnership idea in one line", type: "text" },
      { name: "timeframe", label: "Timeframe", type: "select", options: ["ASAP", "1–3 months", "3–6 months", "Exploring"] },
    ],
  },
];

export const CATEGORY_BY_SLUG = Object.fromEntries(CATEGORIES.map((c) => [c.slug, c]));
